import { ErrorView } from "@/components/ui/layout/error/errorView"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { CustomCheckoutContainer } from "@/modules/payment/components/customCheckoutContainer"
import { useStripePlatform } from "@/modules/payment/hooks/useStripePlatform"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useUser } from "@/modules/users/hooks/useUser"
import {
  useB2BProgramSubscription,
  useB2BProgramUnsubscription,
} from "@modules/boxB2BProgram/hooks/useB2BProgramSubscription"
import { CANCELED_BY_USER } from "@modules/common/types/errorTypes"
import { ConfigService } from "@modules/core/services/configService"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { getMySpacePath, useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { CheckoutContainer } from "@modules/payment/components/checkoutContainer"
import { useCheckoutPayment } from "@modules/payment/hooks/useCheckoutPayment"
import { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import {
  useProgram,
  useProgramRemovePrice,
  useProgramStripeSubscriptionInfos,
} from "@modules/programs/hooks/usePrograms"
import {
  useProgramInfiniteSubscription,
  useProgramInfiniteUnsubscription,
} from "@modules/programs/hooks/useProgramsInfinite"
import { useProgramOnOffUnsubscription } from "@modules/programs/hooks/useProgramsOnOff"
import { useProgramSasRequestTotal } from "@modules/programs/programSas/hooks/useProgramSasRequestTotal"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { loadStripe } from "@stripe/stripe-js"
import { useQueryClient } from "@tanstack/react-query"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

type ProgramAuthorSubscriptionParam = {
  programId: string
}

export const useProgramAuthorSubscription = ({ programId }: ProgramAuthorSubscriptionParam) => {
  const user = useUser()
  const { data: program } = useProgram(programId)
  const { data: sasRequestTotal } = useProgramSasRequestTotal(programId)
  const { navigateToProgramPriceEdition, navigateToProgramPriceSasWarning } = useProgramSidebar()
  const { removeProgramPrice, isRemovingProgramPrice } = useProgramRemovePrice()

  const isStripeAccountComplete = user?.stripeAccountStatus === StripeAccountStatus.OK
  const hasSASRequests = program.entranceCheckEnabled && sasRequestTotal.pendingRequestTotal > 0

  const switchToPaying = () => {
    if (program && isStripeAccountComplete) {
      if (hasSASRequests) {
        navigateToProgramPriceSasWarning(program.id)
      } else {
        navigateToProgramPriceEdition(program.id)
      }
    }
  }

  const switchToFree = () => {
    if (program) {
      removeProgramPrice(program.id)
        .then(() => {
          toast.success(word("program.switchPrice.success"), {
            classNames: successToastClassName(),
          })
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            toast.error(word("global.error.label"), {
              description: e.message,
              classNames: errorToastClassName(),
            })
          } else {
            toast.error(word("global.error.default"), {
              description: String(e),
              classNames: errorToastClassName(),
            })
          }
        })
    }
  }

  return {
    isUpdating: isRemovingProgramPrice,
    switchToFree,
    switchToPaying,
  }
}

export const useProgramAthleteSubscription = (programId: string) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const { data: program } = useProgram(programId)
  const { userFinanceApi } = useServicesContext()
  const boxId = useCurrentBoxId()
  const { subscribeProgramInfinite } = useProgramInfiniteSubscription(programId)
  const { unsubscribeProgramInfinite } = useProgramInfiniteUnsubscription(programId)
  const { unsubscribeProgramOnOff } = useProgramOnOffUnsubscription(programId)
  const { subscribeToBoxB2BProgram } = useB2BProgramSubscription(programId)
  const { unsubscribeToBoxB2BProgram } = useB2BProgramUnsubscription(programId)
  const { checkoutPayment } = useCheckoutPayment()
  const { getStripePublicKey } = useStripePlatform()

  const confirmPopup = useConfirmPopup()
  const user = useUser()
  const mySpaceNavigate = useMySpaceNavigate()
  const { sidebarNavigateTo, sidebarNavigateBack } = useSidebar()
  const queryClient = useQueryClient()

  const isAuthor = user?.id === program.owner?.id
  const isOnOffProgram = program.type === ProgramType.ONOFF

  const { data: programStripeSubscriptionInfos, isLoading } = useProgramStripeSubscriptionInfos(
    program.id,
    program.type,
  )

  const subscribe = async (): Promise<boolean> => {
    const isInPendingPayment = program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT
    const isInactive = program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE
    const canSubscribeToProgram = isInactive || isInPendingPayment
    const canRequestEntranceInSAS = program.entranceCheckEnabled && isInactive

    if (!program || !canSubscribeToProgram) {
      return false
    }
    if (!program.price || canRequestEntranceInSAS) {
      return await joinProgram()
    }
    if (isInPendingPayment || !program.entranceCheckEnabled) {
      return await joinProgramWithPayment()
    }
    return false
  }

  const joinProgram = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      let status: ProgramSubscriptionStatus

      if (boxId && program.type === ProgramType.PRO) {
        status = await subscribeToBoxB2BProgram()
      } else {
        status = await subscribeProgramInfinite()
      }

      await queryClient.invalidateQueries({
        queryKey: ["programStripeSubscriptionInfos", program.id, program.type],
      })

      if (status === ProgramSubscriptionStatus.PENDING_REQUEST) {
        toast.success(word("program.subscription.success.toast"), {
          classNames: successToastClassName(),
        })
      }
      if (status === ProgramSubscriptionStatus.ACTIVE) {
        toast.success(word(isOnOffProgram ? "program.onOff.buy.success.toast" : "program.subscribe.success.toast"), {
          classNames: successToastClassName(),
        })

        setTimeout(() => {
          // Note this is the same route for both virtual and on-off programs
          if (program.type === ProgramType.VIRTUAL) mySpaceNavigate(Path.MySpace.ProgramDetails(program.id))
          else if (program.type === ProgramType.ONOFF) mySpaceNavigate(Path.MySpace.ProgramOnOffDetails(program.id))
        }, 1000)
      }
      return true
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.code === "PAID_PROGRAM_SUBSCRIPTION") {
        console.warn(LoggerType.Program + "try to free subscribe on paid program, refresh program ...")
      } else {
        console.error(LoggerType.Program + "subscribe", e)
        toast.error(word(isOnOffProgram ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"), {
          classNames: errorToastClassName(),
        })
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const joinProgramWithPayment = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await checkoutPayment({
        program,
        returnUrl: getProgramSubscribingPage(),
        shouldReceiveClientSecret: program.type !== ProgramType.PRO,
      })
      const publicKey = await getStripePublicKey(result.coachStripePlatform)
      const stripe = await loadStripe(publicKey, {
        stripeAccount: result.coachStripeAccountId,
      })
      const onCompleteCheckout = () => {
        setIsUpdating(false)
        sidebarNavigateBack()
      }

      if (stripe) {
        if (program.type === ProgramType.PRO && boxId) {
          void sidebarNavigateTo({
            content: (
              <ErrorBoundary fallback={<ErrorView />}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CustomCheckoutContainer
                    clientSecret={result.clientSecret}
                    stripePromise={stripe}
                    program={program}
                    onCompleteCheckout={boxId ? onCompleteCheckout : undefined}
                  />
                </Suspense>
              </ErrorBoundary>
            ),
            leftButtonColor: "var(--surface-background)",
          })
        } else {
          void sidebarNavigateTo({
            content: (
              <ErrorBoundary fallback={<ErrorView />}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CheckoutContainer
                    clientSecret={result.clientSecret}
                    stripePromise={stripe}
                    program={program}
                    onCompleteCheckout={boxId ? onCompleteCheckout : undefined}
                  />
                </Suspense>
              </ErrorBoundary>
            ),
            leftButtonColor: "var(--surface-background)",
          })
        }
      }

      return Promise.resolve(true)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e !== CANCELED_BY_USER) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.code === "PROGRAM_HAS_NO_PRICE") {
          console.warn(LoggerType.Program + "try to paid a free program, refresh program ...")
        } else {
          console.error(LoggerType.Stripe + "subscribe on paid program", e)
          toast.error(word(isOnOffProgram ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"), {
            classNames: errorToastClassName(),
          })
        }
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const tryNewPayment = async (): Promise<boolean> => {
    if (programStripeSubscriptionInfos?.subscriptionId != null) {
      try {
        setIsUpdating(true)

        // TODO : handle the same issue for Boxes
        const url = await userFinanceApi.getPaymentAuthenticationUrl(
          getProgramSubscribingPage(),
          programStripeSubscriptionInfos.subscriptionId,
        )
        window.open(url, "_blank", "noreferrer")
        return Promise.resolve(true)
      } catch (e) {
        console.error(LoggerType.Program + "get authentication url failed", e)
        toast.error(word("program.payment.authentication.error.toast"), {
          classNames: errorToastClassName(),
        })
        return false
      } finally {
        setIsUpdating(false)
      }
    }
    return false
  }

  const openPaymentSettings = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await checkoutPayment({
        program,
        returnUrl: getProgramSubscribingPage(),
        shouldReceiveClientSecret: false,
      })

      window.open(result.url, "_blank", "noreferrer")

      return Promise.resolve(true)
    } catch (e: any) {
      console.error(LoggerType.Program + "get payment settings url failed", e)
      toast.error(word("program.payment.authentication.error.toast"), {
        classNames: errorToastClassName(),
      })
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const unsubscribe = async (): Promise<void> => {
    const isActive = program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE
    const canUnsubscribeToProgram =
      isActive ||
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_REQUEST ||
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
      program.subscriptionStatus === ProgramSubscriptionStatus.TRIALING

    if (program && !isAuthor && canUnsubscribeToProgram) {
      try {
        const processUnsubscribe = async () => {
          setIsUpdating(true)

          if (program.type === ProgramType.VIRTUAL) {
            await unsubscribeProgramInfinite()
          } else if (program.type === ProgramType.PRO && boxId) {
            await unsubscribeToBoxB2BProgram()
          } else if (program.type === ProgramType.PRO) {
            await unsubscribeProgramInfinite()
          } else {
            await unsubscribeProgramOnOff()
          }
          await queryClient.invalidateQueries({
            queryKey: ["programStripeSubscriptionInfos", program.id, program.type],
          })

          toast.success(word("program.unsubscribe.success.toast"), {
            classNames: successToastClassName(),
          })
          setIsUpdating(false)
        }

        if (
          programStripeSubscriptionInfos &&
          "endPeriodDate" in programStripeSubscriptionInfos &&
          programStripeSubscriptionInfos.endPeriodDate
        ) {
          const isTrial = program.subscriptionStatus === ProgramSubscriptionStatus.TRIALING
          const formattedEndDate = getFormatedDateTime(programStripeSubscriptionInfos.endPeriodDate)

          confirmPopup.show({
            title: word("program.subscription.stop.title"),
            message: isTrial
              ? word("program.subscription.stop.trial.body", {
                  endDateSubscription: formattedEndDate,
                })
              : word("program.subscription.stop.paid.body", {
                  endDateSubscription: formattedEndDate,
                }),
            accept: async () => {
              await processUnsubscribe()
            },
            footerProps: {
              confirmText: word("program.subscription.stop.button"),
              cancelText: word("global.cancel"),
            },
          })
        } else if (!isActive) {
          confirmPopup.show({
            title: word("program.details.pendingPayment.popup.title"),
            message: word("program.details.pendingPayment.popup.description"),
            accept: async () => {
              await processUnsubscribe()
            },
          })
        } else {
          await processUnsubscribe()
        }
      } catch (e) {
        console.error(LoggerType.Program + "unsubscribe", e)
        toast.error(word("program.unsubscribe.error.toast"), {
          classNames: errorToastClassName(),
        })
      } finally {
        setIsUpdating(false)
      }
    }
  }

  const getProgramSubscribingPage = () => {
    if (boxId) {
      return window.location.href
    } else if (program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO) {
      return `${ConfigService.config.WEB_URL}${getMySpacePath(Path.MySpace.ProgramSubscribingPage(program.id))}`
    } else if (program.type === ProgramType.ONOFF) {
      return `${ConfigService.config.WEB_URL}${getMySpacePath(Path.MySpace.ProgramOnOffSubscribingPage(program.id))}`
    }
    return ""
  }

  return {
    isUpdating,
    isLoading,
    subscribe,
    unsubscribe,
    joinProgramWithPayment,
    programStripeSubscriptionInfos,
    tryNewPayment,
    openPaymentSettings,
  }
}
